<!-- Social_History.vue — graph-sourced -->
<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <!-- Header -->
    <div class="px-4 py-2.5 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <h3 class="text-base font-semibold text-gray-900">Social History</h3>
        <button
          @click="$emit('refresh')"
          :disabled="loading"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FontAwesomeIcon
            :icon="['fas', 'sync']"
            :class="{ 'animate-spin': loading }"
            class="text-sm"
          />
        </button>
      </div>
    </div>

    <!-- AI Summary Banner -->
    <div v-if="summary" class="px-4 py-3 bg-indigo-50 border-b border-indigo-100">
      <div class="flex items-start gap-2">
        <FontAwesomeIcon :icon="['fas', 'wand-magic-sparkles']" class="text-[10px] text-indigo-500 mt-1 flex-shrink-0" />
        <p class="text-xs text-indigo-800 leading-relaxed">{{ summary }}</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <!-- Empty -->
    <div v-else-if="socialItems.length === 0" class="text-center py-8 px-4">
      <FontAwesomeIcon :icon="['fas', 'seedling']" class="text-2xl text-gray-300 mb-2" />
      <p class="text-sm text-gray-500">No social history data found</p>
      <p class="text-xs text-gray-400 mt-1">SDOH data will appear here when available from EHR</p>
    </div>

    <div v-else class="p-4">
      <!-- Grouped by category -->
      <div class="space-y-3">
        <div v-for="(group, i) in groupedSocial" :key="i">
          <div class="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">
            {{ group.category }}
          </div>
          <div class="space-y-1 pl-2 border-l-2 border-indigo-100">
            <div
              v-for="(item, j) in group.items"
              :key="j"
              class="text-xs py-1"
            >
              <div class="flex items-start gap-2">
                <span class="text-gray-800 font-medium">{{ item.CODE_DISPLAY || item.CODE || 'Unknown' }}</span>
                <span v-if="item.VALUE_STRING" class="text-gray-600">{{ item.VALUE_STRING }}</span>
              </div>
              <div v-if="item.SERVICE_DATE" class="text-[10px] text-gray-400 mt-0.5">
                {{ formatDate(item.SERVICE_DATE) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'Social_History',
  components: { FontAwesomeIcon },
  props: {
    observations: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    summary: { type: String, default: '' }
  },
  emits: ['refresh'],
  computed: {
    socialItems() {
      return this.observations
        .filter(o =>
          (o.CATEGORY || '').toLowerCase() === 'social_history' ||
          (o.CATEGORY || '').toLowerCase() === 'social-history' ||
          (o.CATEGORY || '').toLowerCase() === 'sdoh'
        )
        .sort((a, b) => (b.SERVICE_DATE || '').localeCompare(a.SERVICE_DATE || ''));
    },
    groupedSocial() {
      const map = {};
      for (const item of this.socialItems) {
        const cat = item.CODE_DISPLAY || item.CODE || 'Other';
        if (!map[cat]) map[cat] = { category: cat, items: [] };
        map[cat].items.push(item);
      }
      return Object.values(map);
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    }
  }
};
</script>
